import AsyncStorage from '@react-native-async-storage/async-storage';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Platform, View } from 'react-native';

import { darkColors, lightColors } from '@/config/tokens';

export type ThemeScheme = 'light' | 'dark';
export type Palette = typeof darkColors;

interface ThemeContextValue {
  scheme: ThemeScheme;
  palette: Palette;
  toggle: () => void;
  setScheme: (scheme: ThemeScheme) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  scheme: 'dark',
  palette: darkColors,
  toggle: () => {},
  setScheme: () => {},
});

const STORAGE_KEY = 'tashkeela.theme';

/**
 * App theme (light / dark). Dark is the default so the app looks identical until
 * a user opts in. The choice is persisted. Two things flip together:
 *  - inline colours, via `useThemePalette()` returning the active palette object;
 *  - NativeWind classes, via the `.theme-light` class applied to a root View
 *    (and to <html> on web), which swaps the CSS variables defined in global.css.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [scheme, setSchemeState] = useState<ThemeScheme>('dark');

  useEffect(() => {
    let active = true;
    AsyncStorage.getItem(STORAGE_KEY)
      .then((stored) => {
        if (active && (stored === 'light' || stored === 'dark')) setSchemeState(stored);
      })
      .catch(() => {
        // No stored preference - stay on the dark default.
      });
    return () => {
      active = false;
    };
  }, []);

  const setScheme = (next: ThemeScheme) => {
    setSchemeState(next);
    AsyncStorage.setItem(STORAGE_KEY, next).catch(() => {
      // Non-fatal: the choice just won't survive a restart.
    });
  };

  // Web: toggle the class on <html> so the page background + scrollbar (styled in
  // global.css) follow the theme, not only the in-app content.
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      document.documentElement.classList.toggle('theme-light', scheme === 'light');
    }
  }, [scheme]);

  const palette = scheme === 'light' ? lightColors : darkColors;
  const value = useMemo<ThemeContextValue>(
    () => ({ scheme, palette, toggle: () => setScheme(scheme === 'dark' ? 'light' : 'dark'), setScheme }),
    [scheme, palette],
  );

  return (
    <ThemeContext.Provider value={value}>
      <View
        className={scheme === 'light' ? 'theme-light' : undefined}
        style={{ flex: 1, backgroundColor: palette.gradBottom }}
      >
        {children}
      </View>
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}

/** The active palette object - drop-in for the old static `palette` import. */
export function useThemePalette(): Palette {
  return useContext(ThemeContext).palette;
}
